<template>
    <div class="card">
        <header class="card-header">
            <p class="card-header-title">{{ chartTitle }}</p>

            <div v-if="!isNull(dataType)" class="card-header-icon">
                <span v-tippy :title="$t(dataTypes[dataType].label)">
                    <svg class="svg-icons">
                        <use
                            :xlink:href="
                                require('@/assets/img/icons/' + dataTypes[dataType].svg + '.svg') +
                                '#layer'
                            "
                        ></use>
                    </svg>
                </span>
            </div>
        </header>
        <section class="card-content p-3">
            <div class="content">
                <apexchart
                    v-if="chartVisualization === 'pie'"
                    type="pie"
                    :options="mapOptionsToPieChart"
                    :series="chartData"
                />
                <apexchart
                    v-if="chartVisualization === 'bar'"
                    type="bar"
                    :options="mapOptionsToBarChart"
                    :series="mapDataToBarChart"
                />
                <b-table
                    v-if="chartVisualization === 'grid'"
                    :data="mapDataToGridChart"
                    class="data-table table-overflow"
                >
                    <b-table-column
                        v-for="(f, i) in fields"
                        :key="i"
                        :field="f.key"
                        :label="f.label ? $t(f.label) : $t(f.key)"
                        :width="f.width"
                        :sortable="f.sortable"
                        :centered="f.centered"
                        v-slot="props"
                    >
                        <div
                            :title="props.row[f.key]"
                            v-if="f.key == 'respostas' && cutThis.includes(dataType)"
                        >
                            {{ cutString(props.row[f.key], 100) }}
                        </div>
                        <div v-else>
                            {{ props.row[f.key] }}
                        </div>
                    </b-table-column>
                </b-table>
            </div>
        </section>
        <!-- v-if="!justGrid.includes(dataType)" -->
        <footer class="card-footer" >
            <div
                class="card-footer-item"
                :class="{ selected: chartVisualization === 'pie' }"
                @click="changeChartVisualization('pie')"
            >
                <div class="is-flex g-2">
                    <span class="icon">
                        <i class="fas fa-chart-pie"></i>
                    </span>
                </div>
            </div>
            <div
                class="card-footer-item"
                :class="{ selected: chartVisualization === 'bar' && !isBarChartHorizontal }"
                @click="changeBarChartOrientation('vertical')"
            >
                <div class="is-flex g-2">
                    <span class="icon">
                        <i class="fas fa-chart-column"></i>
                    </span>
                </div>
            </div>
            <div
                class="card-footer-item"
                :class="{ selected: chartVisualization === 'bar' && isBarChartHorizontal }"
                @click="changeBarChartOrientation('horizontal')"
            >
                <div class="is-flex g-2">
                    <span class="icon">
                        <i class="fas fa-chart-bar"></i>
                    </span>
                </div>
            </div>
            <div
                class="card-footer-item"
                :class="{ selected: chartVisualization === 'grid' }"
                @click="changeChartVisualization('grid')"
            >
                <div class="is-flex g-2">
                    <span class="icon">
                        <i class="fas fa-th"></i>
                    </span>
                </div>
            </div>
        </footer>
    </div>
</template>

<script>
import { palette } from '@/functions/color/Palette.js';

export default {
    name: 'ChartViewSelector',
    props: {
        chartTitle: {
            type: String,
            default: '',
        },
        chartLabel: {
            type: Array,
            default: [],
        },
        chartData: {
            type: Array,
            default: [],
        },
        dataType: {
            type: String,
        },
    },
    data() {
        return {
            chartVisualization: 'pie',
            isBarChartHorizontal: false,
            colors: [],
            fields: [
                { key: 'qntde', centered: true, sortable: true, width: 80 },
                { key: 'respostas', label: 'respostas-texto', sortable: true },
            ],
            formattedChartLabels: [],
            dataTypes: {
                TEXT: {
                    svg: 'text-short',
                    label: 'area-texto-curto',
                },
                MULTITEXT: {
                    svg: 'text-subject',
                    label: 'area-texto-longo',
                },
                INTEGER: {
                    svg: 'text-short',
                    label: 'campo-numero',
                },
                DECIMAL: {
                    svg: 'currency-usd',
                    label: 'campo-moeda',
                },
                PERCENT: {
                    svg: 'percent',
                    label: 'campo-porcentagem',
                },
                SWITCH: {
                    svg: 'text-short',
                    label: 'disjuntor',
                },
                DATETIME: {
                    svg: 'calendar-clock',
                    label: 'campo-data-hora',
                },
                DATE: {
                    svg: 'calendar',
                    label: 'calendario',
                },
                RADIO: {
                    svg: 'radiobox-marked',
                    label: 'multipla-escolha',
                },
                CHECKBOX: {
                    svg: 'checkbox-marked',
                    label: 'lista-opcoes',
                },
                COMBOBOX: {
                    svg: 'menu-down',
                    label: 'combo-box',
                },
            },
            justGrid: ['TEXT', 'MULTITEXT'],
            cutThis: ['TEXT', 'MULTITEXT', 'SWITCH', 'RADIO', 'CHECKBOX', 'COMBOBOX'],
        };
    },
    created() {
        if (this.justGrid.includes(this.dataType)) {
            // this.chartVisualization = 'grid';
            this.changeBarChartOrientation('vertical')
        }
        this.colors = palette.getChartColors();

        this.formattedChartLabels = this.chartLabel.map((label) => this.textFormatter(label));
    },
    computed: {
        // configurações
        mapOptionsToPieChart: function () {
            let temp = this;
            return {
                labels: this.formattedChartLabels,
                colors: this.colors,
                legend: {
                    position: 'bottom',
                    horizontalAlign: this.cutThis.includes(this.dataType) ? 'left' : 'center',
                    labels: {
                        useSeriesColors: true,
                    },
                    formatter: function (val) {
                        return temp.cutThis.includes(temp.dataType) ? temp.cutString(val, 65) : val;
                    },
                },
                tooltip: {
                    // enabled: !this.cutThis.includes(this.dataType),
                    y: {
                        title: {
                            formatter: function (val) {
                                return temp.cutThis.includes(temp.dataType)
                                    ? temp.cutString(val)
                                    : val;
                            },
                        },
                    },
                },
            };
        },
        mapOptionsToBarChart: function () {
            const stepSize = this.calculateStepSizeByChartData();
            let temp = this;
            return {
                labels: this.formattedChartLabels,
                colors: this.colors,
                plotOptions: {
                    bar: {
                        horizontal: this.isBarChartHorizontal,
                        borderRadius: 5,
                    },
                },
                xaxis: {
                    stepSize: stepSize,
                    labels: {
                        rotate: -90,
                        trim: true,
                    },
                },
                yaxis: {
                    stepSize: stepSize,
                    labels: {
                        trim: true,
                    },
                },
                tooltip: {
                    x: {
                        formatter: function (val) {
                            return temp.cutThis.includes(temp.dataType) ? temp.cutString(val) : val;
                        },
                    },
                },
            };
        },
        // Dados
        mapDataToBarChart: function () {
            return [
                {
                    name: 'Quantidade',
                    data: this.chartData,
                },
            ];
        },
        mapDataToGridChart: function () {
            return this.chartLabel.map((label, index) => ({
                qntde: this.chartData[index],
                respostas: this.textFormatter(label),
            }));
        },
    },
    methods: {
        changeChartVisualization(chartType) {
            this.chartVisualization = chartType;
        },
        changeBarChartOrientation(orientation) {
            this.changeChartVisualization('bar');
            this.isBarChartHorizontal = orientation === 'horizontal';
        },
        textFormatter(value) {
            if (this.isNull(value)) return this.$t('nao-informado');

            switch (this.dataType) {
                case 'DECIMAL':
                    return this.getDecimal(value);
                case 'PERCENT':
                    return this.parseAsPercent(value);
                case 'DATE':
                    return this.$moment(value).format('L');
                case 'DATETIME':
                    return this.$moment(value).format('L LTS');
                default:
                    return value;
            }
        },
        calculateStepSizeByChartData() {
            const maxValue = Math.max(...this.chartData);
            return Math.ceil(maxValue / 5);
        },
    },
};
</script>

<style lang="scss" scoped>
.table-overflow {
    overflow-y: auto;
    max-height: 385px;
}
.g-2 {
    gap: 8px;
}
.card-footer-item {
    transition: all 0.3s ease-in-out;

    & {
        cursor: pointer;
    }

    &.selected {
        background-color: #f0f0f0;

        .icon {
            color: ($primary-color-click);
        }
    }

    &:hover {
        background-color: #f0f0f0;
    }
}
.apexcharts-tooltip .apexcharts-tooltip-y-group {
    display: flex;
    flex-direction: column;
}
</style>
